/**
 * Cross-player media mutex for book / card previews.
 * Ensures only one audio, video, or YouTube embed plays at a time.
 * Page navigation hands off active playback to hidden shared elements so
 * media continues until it ends naturally.
 */

import {
  pauseActiveCardAudio,
  pauseOtherCardAudioInstances,
  playExclusiveCardAudioTrack,
  registerCardAudioElement,
  releaseCardAudioElement,
  detachCardAudioElementForNavigation,
} from "./singleCardAudio";
import { dismissCardAudioShakaMiniPlayer } from "./cardAudioShakaMiniPlayerStore";
import { ensureMediaNoDownload } from "./protectCardMedia";
import {
  useCardVideoMiniPlayerStore,
  type CardVideoMiniPlayerKind,
  type CardVideoMiniPlayerMeta,
} from "./cardVideoMiniPlayerStore";

export type { CardVideoMiniPlayerMeta, CardVideoMiniPlayerKind };

export const TAAS_CARD_VIDEO_ATTR = "data-taas-card-video";
export const TAAS_CARD_YOUTUBE_ATTR = "data-taas-card-youtube";

const YT_ORIGIN = "https://www.youtube.com";
const YT_NOCOOKIE_ORIGIN = "https://www.youtube-nocookie.com";
const YT_PLAYING = 1;
const YT_PAUSED = 2;
const YT_ENDED = 0;
const YT_ALLOWED_ORIGINS = new Set([YT_ORIGIN, YT_NOCOOKIE_ORIGIN]);

const cardVideoInstances = new Set<HTMLVideoElement>();
const cardYoutubeInstances = new Set<HTMLIFrameElement>();
const exclusiveYoutubeIframes = new Set<HTMLIFrameElement>();
const youtubePlayingIframes = new Set<HTMLIFrameElement>();
const youtubeEverPlayedIframes = new Set<HTMLIFrameElement>();
const youtubePlayers = new Map<
  HTMLIFrameElement,
  {
    pauseVideo: () => void;
    playVideo: () => void;
    destroy: () => void;
    getCurrentTime?: () => number;
    getPlayerState?: () => number;
  }
>();
let youtubeMessageListenerAttached = false;
let youtubeApiReady: Promise<void> | null = null;

type YoutubeWindow = Window &
  typeof globalThis & {
    YT?: {
      Player: new (
        element: string | HTMLElement,
        config: {
          events?: {
            onStateChange?: (event: { data: number }) => void;
          };
        }
      ) => {
        pauseVideo: () => void;
        playVideo: () => void;
        destroy: () => void;
        getCurrentTime?: () => number;
        getPlayerState?: () => number;
      };
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
    };
    onYouTubeIframeAPIReady?: () => void;
  };

function loadYoutubeIframeApi(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (youtubeApiReady) return youtubeApiReady;

  youtubeApiReady = new Promise((resolve) => {
    const win = window as YoutubeWindow;
    if (win.YT?.Player) {
      resolve();
      return;
    }

    const previousReady = win.onYouTubeIframeAPIReady;
    win.onYouTubeIframeAPIReady = () => {
      previousReady?.();
      resolve();
    };

    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const tag = document.createElement("script");
      tag.src = "https://www.youtube.com/iframe_api";
      tag.async = true;
      document.head.appendChild(tag);
    }
  });

  return youtubeApiReady;
}

function handleYoutubeExclusivePlay(activeIframe: HTMLIFrameElement) {
  const playingInMiniPlayer =
    activeIframe === miniPlayerYoutubeIframe && useCardVideoMiniPlayerStore.getState().visible;

  if (!playingInMiniPlayer) {
    retireCardVideoMiniPlayerForInPagePlayback(activeIframe);
  }

  pauseOtherCardHtmlMediaForYoutube();
  youtubePlayers.forEach((player, iframe) => {
    if (iframe !== activeIframe) {
      try {
        player.pauseVideo();
      } catch {
        /* ignore */
      }
    }
  });
  exclusiveYoutubeIframes.forEach((iframe) => {
    if (iframe !== activeIframe) {
      pauseCardYoutubeIframe(iframe);
    }
  });
}

function bindYoutubeExclusivePlayer(iframe: HTMLIFrameElement) {
  void loadYoutubeIframeApi().then(() => {
    const win = window as YoutubeWindow;
    if (!win.YT?.Player || youtubePlayers.has(iframe)) return;

    try {
      const player = new win.YT.Player(iframe, {
        events: {
          onStateChange: (event) => {
            if (event.data === win.YT!.PlayerState.PLAYING) {
              youtubePlayingIframes.add(iframe);
              youtubeEverPlayedIframes.add(iframe);
              handleYoutubeExclusivePlay(iframe);
            } else if (event.data === YT_PAUSED || event.data === YT_ENDED) {
              youtubePlayingIframes.delete(iframe);
            }
          },
        },
      });
      youtubePlayers.set(iframe, player);
    } catch {
      /* postMessage fallback remains active */
    }
  });
}

function unbindYoutubeExclusivePlayer(iframe: HTMLIFrameElement) {
  const player = youtubePlayers.get(iframe);
  if (!player) return;
  youtubePlayers.delete(iframe);
  try {
    player.destroy();
  } catch {
    /* ignore */
  }
}

export function cardYoutubeIframeDomId(sourceUrl: string): string {
  const slug = sourceUrl.replace(/[^a-zA-Z0-9]+/g, "").slice(-28);
  return `taas-card-yt-${slug || "embed"}`;
}

function parseYoutubeMessage(data: unknown): {
  event?: string;
  info?: number;
  id?: string;
} | null {
  if (data == null) return null;
  if (typeof data === "string") {
    try {
      return JSON.parse(data) as { event?: string; info?: number; id?: string };
    } catch {
      return null;
    }
  }
  if (typeof data === "object") {
    return data as { event?: string; info?: number; id?: string };
  }
  return null;
}

/** Soft-pause via YouTube postMessage (requires enablejsapi=1 on embed URL). */
export function pauseCardYoutubeIframe(iframe: HTMLIFrameElement) {
  const player = youtubePlayers.get(iframe);
  if (player) {
    try {
      player.pauseVideo();
      return;
    } catch {
      /* fall through to postMessage */
    }
  }

  const payload = JSON.stringify({
    event: "command",
    func: "pauseVideo",
    args: "",
  });
  iframe.contentWindow?.postMessage(payload, YT_ORIGIN);
  iframe.contentWindow?.postMessage(payload, YT_NOCOOKIE_ORIGIN);
}

/** Resume YouTube playback after reparenting (mini player / page handoff). */
export function playCardYoutubeIframe(iframe: HTMLIFrameElement) {
  const postYoutubeCommand = (func: "playVideo" | "pauseVideo") => {
    const payload = JSON.stringify({
      event: "command",
      func,
      args: "",
    });
    iframe.contentWindow?.postMessage(payload, YT_ORIGIN);
    iframe.contentWindow?.postMessage(payload, YT_NOCOOKIE_ORIGIN);
  };

  const postListening = () => {
    const payload = JSON.stringify({
      event: "listening",
      id: iframe.id,
      channel: "widget",
    });
    iframe.contentWindow?.postMessage(payload, YT_ORIGIN);
    iframe.contentWindow?.postMessage(payload, YT_NOCOOKIE_ORIGIN);
  };

  const markPlaying = () => {
    youtubePlayingIframes.add(iframe);
    youtubeEverPlayedIframes.add(iframe);
  };

  const player = youtubePlayers.get(iframe);
  if (player) {
    try {
      player.playVideo();
      markPlaying();
      return;
    } catch {
      /* fall through to postMessage */
    }
  }

  postListening();
  postYoutubeCommand("playVideo");
  markPlaying();

  // Reparenting can pause the widget — retry once the iframe settles in the mini player.
  window.setTimeout(() => {
    postListening();
    postYoutubeCommand("playVideo");
  }, 120);
  window.setTimeout(() => {
    postListening();
    postYoutubeCommand("playVideo");
  }, 450);
}

function pauseOtherCardHtmlMediaForYoutube() {
  pauseActiveCardAudio();
  if (typeof document === "undefined") return;
  document.querySelectorAll(`video[${TAAS_CARD_VIDEO_ATTR}]`).forEach((node) => {
    if (node instanceof HTMLVideoElement && !node.paused) {
      node.pause();
    }
  });
}

function resolveYoutubeIframeFromMessage(
  id: string | undefined,
  source: MessageEventSource | null
): HTMLIFrameElement | null {
  if (id) {
    const byId = document.getElementById(id);
    if (byId instanceof HTMLIFrameElement) return byId;
  }
  if (source) {
    for (const iframe of exclusiveYoutubeIframes) {
      if (iframe.contentWindow === source) return iframe;
    }
  }
  return null;
}

function isYoutubePlayingMessage(data: { event?: string; info?: unknown }): boolean {
  if (data.event === "onStateChange") {
    return data.info === YT_PLAYING;
  }
  if (data.event === "infoDelivery" && data.info && typeof data.info === "object") {
    return (data.info as { playerState?: number }).playerState === YT_PLAYING;
  }
  return false;
}

function isYoutubePausedOrEndedMessage(data: { event?: string; info?: unknown }): boolean {
  if (data.event === "onStateChange") {
    return data.info === YT_PAUSED || data.info === YT_ENDED;
  }
  if (data.event === "infoDelivery" && data.info && typeof data.info === "object") {
    const state = (data.info as { playerState?: number }).playerState;
    return state === YT_PAUSED || state === YT_ENDED;
  }
  return false;
}

function handleYoutubeWindowMessage(event: MessageEvent) {
  if (!YT_ALLOWED_ORIGINS.has(event.origin)) return;
  const data = parseYoutubeMessage(event.data);
  if (!data || !isYoutubePlayingMessage(data)) return;

  const activeIframe = resolveYoutubeIframeFromMessage(data.id, event.source);
  if (!activeIframe) return;
  if (isYoutubePlayingMessage(data)) {
    youtubePlayingIframes.add(activeIframe);
    youtubeEverPlayedIframes.add(activeIframe);
    handleYoutubeExclusivePlay(activeIframe);
    return;
  }
  if (isYoutubePausedOrEndedMessage(data)) {
    youtubePlayingIframes.delete(activeIframe);
  }
}

function ensureYoutubeMessageListener() {
  if (youtubeMessageListenerAttached || typeof window === "undefined") return;
  window.addEventListener("message", handleYoutubeWindowMessage);
  youtubeMessageListenerAttached = true;
}

function removeYoutubeMessageListenerIfIdle() {
  if (!youtubeMessageListenerAttached || exclusiveYoutubeIframes.size > 0) {
    return;
  }
  window.removeEventListener("message", handleYoutubeWindowMessage);
  youtubeMessageListenerAttached = false;
}

function registerExclusiveCardYoutubeIframe(iframe: HTMLIFrameElement, sourceUrl?: string) {
  iframe.id = sourceUrl
    ? cardYoutubeIframeDomId(sourceUrl)
    : iframe.id || `taas-card-yt-${Math.random().toString(36).slice(2, 10)}`;
  exclusiveYoutubeIframes.add(iframe);
  ensureYoutubeMessageListener();
  bindYoutubeExclusivePlayer(iframe);

  // Ask the widget to emit state events (needed for play detection / mini-player handoff).
  const listening = JSON.stringify({
    event: "listening",
    id: iframe.id,
    channel: "widget",
  });
  try {
    iframe.contentWindow?.postMessage(listening, YT_ORIGIN);
    iframe.contentWindow?.postMessage(listening, YT_NOCOOKIE_ORIGIN);
  } catch {
    /* ignore */
  }
}

function unregisterExclusiveCardYoutubeIframe(iframe: HTMLIFrameElement) {
  exclusiveYoutubeIframes.delete(iframe);
  unbindYoutubeExclusivePlayer(iframe);
  removeYoutubeMessageListenerIfIdle();
}

function pauseOtherExclusiveYoutubeIframes(current?: HTMLIFrameElement | null) {
  exclusiveYoutubeIframes.forEach((iframe) => {
    if (iframe !== current) {
      pauseCardYoutubeIframe(iframe);
    }
  });
}

function pauseAllExclusiveYoutubeIframes() {
  pauseOtherExclusiveYoutubeIframes(null);
}

let sharedSingleCardVideo: HTMLVideoElement | null = null;
let persistentYoutubeHost: HTMLDivElement | null = null;
let miniPlayerYoutubeIframe: HTMLIFrameElement | null = null;
let miniPlayerMediaHost: HTMLElement | null = null;

const HIDDEN_MEDIA_STYLE =
  "position:fixed;width:1px;height:1px;opacity:0;pointer-events:none;left:-9999px;top:0;z-index:-1;";

const MINI_PLAYER_VIDEO_STYLE =
  "position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;background:#000;";

const getPersistentYoutubeHost = (): HTMLDivElement | null => {
  if (typeof document === "undefined") return null;
  if (!persistentYoutubeHost) {
    persistentYoutubeHost = document.createElement("div");
    persistentYoutubeHost.id = "taas-card-youtube-persist";
    persistentYoutubeHost.style.cssText = HIDDEN_MEDIA_STYLE;
    document.body.appendChild(persistentYoutubeHost);
  }
  return persistentYoutubeHost;
};

const getHiddenMediaSink = (): HTMLElement => {
  return getPersistentYoutubeHost() ?? document.body;
};

const getSharedSingleCardVideo = (): HTMLVideoElement | null => {
  if (typeof document === "undefined") return null;
  if (!sharedSingleCardVideo) {
    sharedSingleCardVideo = document.createElement("video");
    sharedSingleCardVideo.preload = "metadata";
    sharedSingleCardVideo.playsInline = true;
    sharedSingleCardVideo.controls = false;
    sharedSingleCardVideo.style.cssText = HIDDEN_MEDIA_STYLE;
    document.body.appendChild(sharedSingleCardVideo);
  }
  return sharedSingleCardVideo;
};

const isYoutubeIframePlaying = (iframe: HTMLIFrameElement) => {
  if (youtubePlayingIframes.has(iframe) || youtubeEverPlayedIframes.has(iframe)) {
    return true;
  }
  const player = youtubePlayers.get(iframe);
  try {
    // PLAYING=1, BUFFERING=3 — both mean the user started playback.
    const state = player?.getPlayerState?.();
    return state === 1 || state === 3;
  } catch {
    return false;
  }
};

const parkSharedSingleCardVideo = () => {
  const shared = sharedSingleCardVideo;
  if (!shared) return;
  shared.style.cssText = HIDDEN_MEDIA_STYLE;
  getHiddenMediaSink().appendChild(shared);
};

const parkMiniPlayerYoutubeIframe = () => {
  const iframe = miniPlayerYoutubeIframe;
  if (!iframe) return;
  iframe.style.cssText = HIDDEN_MEDIA_STYLE;
  getHiddenMediaSink().appendChild(iframe);
  // Keep the ref so attach/dismiss can still find it (do not null here).
};

/**
 * Stop floating mini-player media when playback moves back onto a book page,
 * or before promoting a different clip. Does not pause `exemptYoutube`.
 */
function retireCardVideoMiniPlayerForInPagePlayback(exemptYoutube?: HTMLIFrameElement | null) {
  const shared = sharedSingleCardVideo;
  if (shared && !shared.paused) {
    shared.pause();
  }
  parkSharedSingleCardVideo();

  const youtubePlayingInMiniPlayer =
    Boolean(exemptYoutube) && exemptYoutube === miniPlayerYoutubeIframe;

  if (miniPlayerYoutubeIframe && miniPlayerYoutubeIframe !== exemptYoutube) {
    pauseCardYoutubeIframe(miniPlayerYoutubeIframe);
    parkMiniPlayerYoutubeIframe();
  }

  // Keep the floating mini player alive while YouTube continues there.
  if (youtubePlayingInMiniPlayer) {
    return;
  }

  miniPlayerMediaHost = null;
  if (useCardVideoMiniPlayerStore.getState().visible) {
    useCardVideoMiniPlayerStore.getState().hide();
  }
}

const normalizeMediaSrc = (value: string) => {
  if (!value) return "";
  try {
    return new URL(value, window.location.href).href;
  } catch {
    return value;
  }
};

const handoffToSharedSingleCardVideo = (source: HTMLVideoElement) => {
  const shared = getSharedSingleCardVideo();
  if (!shared || shared === source) return;
  if (source.paused || source.ended) return;

  const src = source.currentSrc || source.src;
  if (!src) return;

  const resumeAt = Number(source.currentTime) || 0;
  const nextSrc = normalizeMediaSrc(src);
  const currentSharedSrc = normalizeMediaSrc(shared.currentSrc || shared.src || "");

  shared.muted = source.muted;
  source.pause();

  const resumeAndPlay = () => {
    try {
      if (Number.isFinite(resumeAt) && resumeAt > 0) {
        shared.currentTime = resumeAt;
      }
    } catch {
      /* ignore seek errors before ready */
    }
    void shared.play().catch(() => {});
  };

  // After a Shaka audio <video> was torn down, immediate seek+play after load()
  // often resets to 0 — wait for loadeddata so the mini player continues.
  if (currentSharedSrc !== nextSrc) {
    const onReady = () => resumeAndPlay();
    shared.addEventListener("loadeddata", onReady, { once: true });
    shared.src = src;
    shared.load();
    if (shared.readyState >= 2) {
      shared.removeEventListener("loadeddata", onReady);
      resumeAndPlay();
    }
    return;
  }

  resumeAndPlay();
};

export const promoteCardVideoToMiniPlayer = (
  source: HTMLVideoElement,
  meta?: CardVideoMiniPlayerMeta
) => {
  if (source.paused || source.ended) return;
  retireCardVideoMiniPlayerForInPagePlayback();
  handoffToSharedSingleCardVideo(source);
  cardVideoInstances.delete(source);
  useCardVideoMiniPlayerStore.getState().show("file", meta);
};

/**
 * After in-page Shaka playback: continue in the mini player with a fresh Shaka
 * instance (native <video> cannot play many adaptive streams Shaka handles).
 */
export const promoteCardShakaToMiniPlayer = (
  mediaUrl: string,
  startTime: number,
  meta?: CardVideoMiniPlayerMeta
) => {
  const url = String(mediaUrl || "").trim();
  if (!url) return;

  retireCardVideoMiniPlayerForInPagePlayback();
  parkSharedSingleCardVideo();
  parkMiniPlayerYoutubeIframe();
  useCardVideoMiniPlayerStore.getState().show("shaka", {
    ...meta,
    mediaUrl: url,
    startTime: Math.max(0, startTime || 0),
  });
};

export const promoteCardYoutubeToMiniPlayer = (
  iframe: HTMLIFrameElement,
  meta?: CardVideoMiniPlayerMeta
) => {
  retireCardVideoMiniPlayerForInPagePlayback();
  cardYoutubeInstances.delete(iframe);

  // Swap in a placeholder so React's unmount removes that node — not our iframe.
  if (iframe.parentNode) {
    const placeholder = document.createElement("span");
    placeholder.setAttribute("data-taas-yt-handoff", "1");
    placeholder.style.display = "none";
    iframe.parentNode.replaceChild(placeholder, iframe);
  }

  miniPlayerYoutubeIframe = iframe;
  iframe.style.cssText = HIDDEN_MEDIA_STYLE;
  getHiddenMediaSink().appendChild(iframe);

  useCardVideoMiniPlayerStore.getState().show("youtube", {
    ...meta,
    embedUrl: meta?.embedUrl || iframe.src,
    mediaUrl: meta?.mediaUrl || iframe.src,
  });
};

export const attachCardVideoMiniPlayerMedia = (
  host: HTMLElement,
  kind: CardVideoMiniPlayerKind
) => {
  miniPlayerMediaHost = host;

  // Shaka is rendered by React inside the host — only park other media.
  if (kind === "shaka") {
    parkMiniPlayerYoutubeIframe();
    parkSharedSingleCardVideo();
    return;
  }

  if (kind === "file") {
    parkMiniPlayerYoutubeIframe();
    const shared = getSharedSingleCardVideo();
    if (!shared) return;
    shared.style.cssText = MINI_PLAYER_VIDEO_STYLE;
    shared.controls = true;
    shared.setAttribute("controlsList", "nodownload");
    ensureMediaNoDownload(shared);
    host.appendChild(shared);
    if (shared.paused) void shared.play().catch(() => {});
    return;
  }

  if (kind === "youtube") {
    parkSharedSingleCardVideo();
    const iframe = miniPlayerYoutubeIframe;
    if (!iframe) return;
    iframe.style.cssText =
      "position:absolute;inset:0;width:100%;height:100%;border:none;display:block;";
    host.appendChild(iframe);
    // Reparenting pauses YouTube — resume like the file mini-player.
    requestAnimationFrame(() => {
      void loadYoutubeIframeApi().then(() => {
        playCardYoutubeIframe(iframe);
      });
    });
  }
};

export const detachCardVideoMiniPlayerMedia = () => {
  miniPlayerMediaHost = null;
  parkSharedSingleCardVideo();
  parkMiniPlayerYoutubeIframe();
};

export const dismissCardVideoMiniPlayer = () => {
  const shared = sharedSingleCardVideo;
  if (shared && !shared.paused) shared.pause();
  if (miniPlayerYoutubeIframe) {
    pauseCardYoutubeIframe(miniPlayerYoutubeIframe);
    unregisterExclusiveCardYoutubeIframe(miniPlayerYoutubeIframe);
    youtubePlayingIframes.delete(miniPlayerYoutubeIframe);
    youtubeEverPlayedIframes.delete(miniPlayerYoutubeIframe);
    miniPlayerYoutubeIframe = null;
  }
  detachCardVideoMiniPlayerMedia();
  useCardVideoMiniPlayerStore.getState().hide();
};

export const isCardVideoMiniPlayerVisible = () => useCardVideoMiniPlayerStore.getState().visible;

export const registerCardVideoElement = (video: HTMLVideoElement) => {
  cardVideoInstances.add(video);
  if (typeof video.setAttribute === "function") {
    video.setAttribute(TAAS_CARD_VIDEO_ATTR, "1");
  }
  ensureMediaNoDownload(video);
};

export const registerCardYoutubeEmbed = (iframe: HTMLIFrameElement, sourceUrl?: string) => {
  cardYoutubeInstances.add(iframe);
  iframe.setAttribute(TAAS_CARD_YOUTUBE_ATTR, "1");
  registerExclusiveCardYoutubeIframe(iframe, sourceUrl);
};

export {
  CARD_MEDIA_CONTROLS_LIST,
  CARD_MEDIA_CONTROLS_LIST as CARD_VIDEO_CONTROLS_LIST,
  cardProtectedImageProps,
  cardProtectedImageSx,
  cardProtectedImageStyle,
  ensureMediaNoDownload,
  preventCardMediaContextMenu,
} from "./protectCardMedia";

/** Pause and unregister — use when explicitly stopping playback. */
export const releaseCardVideoElement = (video: HTMLVideoElement) => {
  cardVideoInstances.delete(video);
  video.pause();
};

/**
 * Unregister page-local video on navigation without stopping playback.
 */
export const detachCardVideoElementForNavigation = (
  video: HTMLVideoElement,
  meta?: CardVideoMiniPlayerMeta
) => {
  cardVideoInstances.delete(video);
  if (!video.paused && !video.ended) {
    if (meta) {
      promoteCardVideoToMiniPlayer(video, meta);
      return;
    }
    handoffToSharedSingleCardVideo(video);
  }
};

/**
 * Unregister a page-local YouTube iframe on unmount.
 * Reparents to the mini player when playback is active.
 */
export const detachCardYoutubeEmbedForNavigation = (
  iframe: HTMLIFrameElement,
  meta?: CardVideoMiniPlayerMeta
) => {
  cardYoutubeInstances.delete(iframe);
  if (meta && isYoutubeIframePlaying(iframe)) {
    promoteCardYoutubeToMiniPlayer(iframe, meta);
    return;
  }
  unregisterExclusiveCardYoutubeIframe(iframe);
};

export const releaseCardYoutubeEmbed = (iframe: HTMLIFrameElement) => {
  cardYoutubeInstances.delete(iframe);
  unregisterExclusiveCardYoutubeIframe(iframe);
  iframe.src = "about:blank";
};

/** Pause every registered / marked video except `current` (if provided). */
export const pauseOtherCardVideos = (current?: HTMLVideoElement | null) => {
  cardVideoInstances.forEach((el) => {
    if (el !== current && !el.paused) {
      el.pause();
    }
  });

  const shared = getSharedSingleCardVideo();
  if (shared && shared !== current && !shared.paused) {
    shared.pause();
  }

  if (typeof document !== "undefined") {
    document.querySelectorAll(`video[${TAAS_CARD_VIDEO_ATTR}], video`).forEach((node) => {
      if (!(node instanceof HTMLVideoElement)) return;
      if (current && node === current) return;
      if (node === shared) return;
      // Hidden Shaka-audio engine is dismissed explicitly when video/YT starts.
      // Skipping it here avoids touching it during video exclusive play/handoff.
      if (node.hasAttribute("data-taas-card-shaka-audio")) return;
      if (!node.paused) node.pause();
    });
  }
};

/** Pause every registered YouTube iframe except `current` (if provided). */
export const pauseOtherCardYoutubeEmbeds = (current?: HTMLIFrameElement | null) => {
  pauseOtherExclusiveYoutubeIframes(current);
  cardYoutubeInstances.forEach((iframe) => {
    if (iframe !== current) {
      pauseCardYoutubeIframe(iframe);
    }
  });
};

/** Pause all card videos (e.g. when starting audio or a new exclusive clip). */
export const pauseAllCardVideos = () => {
  pauseOtherCardVideos(null);
};

export const pauseAllCardYoutubeEmbeds = () => {
  pauseAllExclusiveYoutubeIframes();
  cardYoutubeInstances.forEach((iframe) => {
    pauseCardYoutubeIframe(iframe);
  });
};

/**
 * Start exclusive playback: pause sibling videos, YouTube embeds, and card audio,
 * then play `media` (video or audio).
 */
export const playExclusiveCardMedia = async (media: HTMLMediaElement): Promise<void> => {
  pauseAllCardYoutubeEmbeds();

  if (media instanceof HTMLVideoElement) {
    retireCardVideoMiniPlayerForInPagePlayback();
    dismissCardAudioShakaMiniPlayer();
    pauseOtherCardVideos(media);
    pauseActiveCardAudio();
    registerCardVideoElement(media);
  } else if (media instanceof HTMLAudioElement) {
    dismissCardVideoMiniPlayer();
    dismissCardAudioShakaMiniPlayer();
    pauseAllCardVideos();
    const audioUrl = media.currentSrc || media.src || "";
    await playExclusiveCardAudioTrack(media, audioUrl);
    return;
  }

  await media.play();
};

/** Start exclusive YouTube playback — stops other card media first. */
export const playExclusiveCardYoutube = (iframe: HTMLIFrameElement) => {
  retireCardVideoMiniPlayerForInPagePlayback(iframe);
  dismissCardAudioShakaMiniPlayer();
  pauseAllCardVideos();
  pauseActiveCardAudio();
  pauseOtherCardYoutubeEmbeds(iframe);
  registerCardYoutubeEmbed(iframe);
};

/** Stop every card video, YouTube embed, and audio — use on modal close / route exit. */
export const pauseAllCardMedia = () => {
  dismissCardVideoMiniPlayer();
  dismissCardAudioShakaMiniPlayer();
  pauseAllCardVideos();
  pauseAllCardYoutubeEmbeds();
  pauseActiveCardAudio();
  youtubePlayingIframes.clear();
  youtubeEverPlayedIframes.clear();
};

export { registerCardAudioElement, releaseCardAudioElement, detachCardAudioElementForNavigation };
